import { Component, OnInit, ViewChild } from '@angular/core';
import { ITimeLine } from '../../../../shared/model/timeline.dto';
import { AuthService, UserInvitation } from '../../../../root/services/auth.service';
import { ActivatedRoute } from '@angular/router';
import { SignUpTimelineService } from '../../../services/sign-up-timeline.service';
import { SignUpStateService } from '../../../services/sign-up-state.service';
import { CommsPreferencesDTO } from '../../../../shared/model/communication-preferences.dto';
import { SignUpPasswordComponent } from '../../sign-up-password/sign-up-password.component';
import { animate, style, transition, trigger } from '@angular/animations';
import { catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { NavigationService } from '../../../../root/services/navigation.service';

@Component({
  selector: 'hfc-sign-up-steps',
  templateUrl: './sign-up-steps.component.html',
  styleUrls: ['./sign-up-steps.component.scss'],
  animations: [
    trigger('containerResize', [
      transition(':enter, :increment, :decrement', [
        style({ opacity: 0 }),
        animate('200ms ease-in', style({ opacity: 1 }))
      ])
    ])
  ]
})
export class SignUpStepsComponent implements OnInit {
  @ViewChild('passwordForm') passwordForm!: SignUpPasswordComponent;

  public timeLineContent: ITimeLine[] = [];
  public currentStep: number | null = null;
  public userInvited?: UserInvitation;
  public tokenExpired = false;
  private termsAndConditionsDeclined = false;

  constructor(
    public readonly signUpStateService: SignUpStateService,
    private readonly authService: AuthService,
    private readonly route: ActivatedRoute,
    private readonly signUpTimelineService: SignUpTimelineService,
    private readonly navigationService: NavigationService
  ) {}

  public get tokenId() {
    return this.route.snapshot.queryParamMap.get('token');
  }

  ngOnInit(): void {
    this.timeLineContent = this.signUpTimelineService.getDefaultTimeline();
    const tokenId = this.tokenId;

    if (!tokenId) {
      // No token in URL, fallback to state-based initialization
      this.signUpStateService.initialize().subscribe();
    } else {
      // Proceed with token-based invitation flow
      this.signUpStateService.userTokenId$.next(tokenId);

      this.authService
        .getInvitationDataObservable(tokenId)
        .pipe(
          tap((invitationData: UserInvitation) => {
            this.userInvited = invitationData;
            this.currentStep = 0;
          }),
          catchError(() => {
            // Fallback if invitation fails
            this.navigationService.goToLogin().then(); // redirect
            return of(null);
          })
        )
        .subscribe();
    }

    this.signUpStateService.getCurrentStep$.subscribe((step) => {
      if (typeof step === 'number') {
        this.currentStep = step;
        this.signUpTimelineService.markStepComplete(this.currentStep - 1);
      }
    });
  }

  public getPassword(passwordSet: string) {
    this.signUpStateService.password$.next(passwordSet);
  }

  public setPassword(): void {
    this.passwordForm.submitPasswordIfValid();
    const password = this.signUpStateService.password$.value;
    if (password) {
      this.signUpStateService.setPassword(password);
    }
  }

  public setupTwoFactorAuthenticationCompleted() {
    this.signUpStateService.updateIsSubmitting$.next(true);
    this.signUpStateService.setTwoFactorAuthentication();
  }

  public acceptTermsAndConditions() {
    this.signUpStateService.acceptTermsAndConditions();
  }

  public declineTermsAndConditions(): void {
    this.termsAndConditionsDeclined = !this.termsAndConditionsDeclined;
    this.signUpStateService.termsAndConditionsAreDeclined$.next(this.termsAndConditionsDeclined);
  }

  public confirmCommsPreferences($event: CommsPreferencesDTO): void {
    this.signUpStateService.setCommsPreferences($event);
  }

  public onStepComplete(stepIndex: number): void {
    this.currentStep = stepIndex + 1;
    this.signUpTimelineService.setActiveStep(this.currentStep);
  }
}
